import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, ShieldCheck } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { MaskLine, scrollToId } from "@/lib/ui";

export default function Hero() {
  const { t } = useLang();

  return (
    <section
      id="acasa"
      className="relative min-h-[78svh] sm:min-h-[85svh] lg:min-h-[100svh] flex items-end overflow-hidden noise"
    >
      <div className="absolute inset-0">
        <img src="/poza11.webp" alt="" className="h-full w-full object-cover object-right md:object-center" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/40" />
      <div className="absolute inset-0 grid-lines opacity-40" />

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 lg:px-8 pb-16 lg:pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-sky-300"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-sky-400 node-pulse" />
          {t({ ro: "Profesionalism în mișcare", en: "Professionalism in motion" })}
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tighter text-white text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.92] max-w-5xl">
          <MaskLine delay={0.5}>{t({ ro: "Transport internațional", en: "International freight" })}</MaskLine>
          <MaskLine delay={0.65}>
            <span className="text-gradient">{t({ ro: "de marfă în Europa", en: "transport across Europe" })}</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          {t({
            ro: "Din Dumbrăveni, Suceava, livrăm siguranță — nu doar marfă. Logistică adaptată afacerii tale, monitorizare GPS în timp real și acoperire în întreaga Uniune Europeană.",
            en: "From Dumbrăveni, Suceava, we deliver safety — not just cargo. Logistics tailored to your business, real-time GPS tracking and coverage across the entire European Union.",
          })}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-quote"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center gap-3 rounded-full bg-blue-600 px-7 py-4 font-semibold text-white hover:bg-blue-500 transition-colors"
          >
            {t({ ro: "Cere ofertă", en: "Request a quote" })}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-fleet"
            onClick={() => scrollToId("flota")}
            className="inline-flex items-center gap-3 rounded-full glass px-7 py-4 font-semibold text-white hover:border-white/30 transition-colors"
          >
            <PlayCircle className="h-5 w-5 text-sky-400" />
            {t({ ro: "Vezi flota", en: "Explore fleet" })}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-12 flex items-center gap-3 text-sm text-slate-400"
        >
          <ShieldCheck className="h-5 w-5 text-sky-400" />
          {t({ ro: "Asigurare CMR extinsă · Normă EURO 6 · Disponibilitate 24/7", en: "Extended CMR insurance · EURO 6 standard · 24/7 availability" })}
        </motion.div>
      </div>
    </section>
  );
}
